import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** Default when `environment.hygieneDomain` is not set */
const DEFAULT_DOMAIN = '';

/** Fallback JWT when no token in storage (dev / embedded WebView) */
const STATIC_DAY_WISE_JWT ='';

export interface HygieneLocationFilters {
  /** Single-value query — used when `collegeTypes` / `boardingTypes` are omitted */
  schooolOrCollege?: string;
  boardingType?: string;
  /** Multi-select — serialized as comma-separated `collegeType` / `campusType` (API supports multiple) */
  collegeTypes?: ('SCHOOL' | 'COLLEGE')[];
  boardingTypes?: ('RESIDENTIAL' | 'DAY_SCHOLAR')[];
  /** Optional vendor filter when APIs support it */
  vendorId?: string;
}

export interface HygieneStaffAttendanceDay {
  date: string;
  att: string;
}

export interface HygieneStaffAttendanceCampus {
  campusId?: number;
  campusName?: string;
  stateId?: number;
  stateName?: string;
  cityId?: number;
  cityName?: string;
  fusionBuildingCodeProd?:string | number | null;
  buildingId?: number;
  buildingName?: string;
  vendorName?: string | null;
  attendance?: HygieneStaffAttendanceDay[];
  /** Pay / salary period before last paid — API may send string or numeric YYYYMM (e.g. 202603). */
  period?: string | number | null;
  lastPaidPeriod?: string | number | null;
  payPeriod?: string | number | null;
  salaryPeriod?: string | number | null;
  /** Present when API returns payroll-style totals per row */
  avgPaidAmount?: number | null;
  estPaidAmount?: number | null;
  /** Building geolocation — used to open map from day-wise grid */
  latitude?: number | string | null;
  longitude?: number | string | null;
}

export interface StaffAttendanceImage {
  url?: string;
  uplodedTime?: string;
  uploadedTime?: string;
}

/** Vendor row from GET …/apiV2/vendorList — `vendorId` kept as string to avoid JS int precision loss */
export interface HygieneDayWiseVendor {
  vendorId: string;
  vendorName: string;
}

/** Department options for day-wise UI + API `departmentName` (uppercase). Single place to edit. */
export const HYGIENE_DAY_WISE_DEPARTMENTS: readonly { id: number; name: string }[] = [
  { id: 1, name: 'HOUSEKEEPING' },
  { id: 4, name: 'KITCHEN' },
  { id: 3, name: 'MAINTENANCE' },
  { id: 2, name: 'SANITATION' },
  { id: 5, name: 'SECURITY' },
];

@Injectable({
  providedIn: 'root'
})
export class HygineDayWiseService {
  private readonly API_ORIGIN = String((environment as any).masterDomain || DEFAULT_DOMAIN).trim().replace(/\/+$/, '');

 private readonly statesUrl = `${this.API_ORIGIN}/apiV2/hygiene/employeeStates`;
  private readonly citiesUrl = `${this.API_ORIGIN}/apiV2/hygiene/empStateCities`;
  private readonly empCitiesUrl = `${this.API_ORIGIN}/apiV2/hygiene/empCities`;
  private readonly campusesUrl = `${this.API_ORIGIN}/apiV2/hygiene/employeeCampuses`;
  private readonly buildingsUrl = `${this.API_ORIGIN}/apiV2/hygiene/empCmpBuilding`;
  private readonly staffAttendanceUrl =`${this.API_ORIGIN}/apiV2/hygiene/staffAttendance`;
  private readonly staffAttendanceExcel = `${this.API_ORIGIN}/apiV2/hygiene/staffAttendance/excelReport`;
  private readonly staffAttendanceImagesUrl =`${this.API_ORIGIN}/employee/api/jwtV2/hygiene/getStaffAttendanceImages`;
  private readonly vendorListUrl = `${this.API_ORIGIN}/apiV2/vendorList`;

  private readonly tokenStorageKeys = ['updated', 'jwtToken', 'token', 'authToken', 'accessToken'] as const;

  constructor(private http: HttpClient) {}

  private get headers(): HttpHeaders {
    let h = new HttpHeaders({ 'Content-Type': 'application/json' });
    const auth = this.authHeaderValue;
    if (auth) h = h.set('Authorization', auth);
    return h;
  }

  private get authHeaderValue(): string {
    let raw: string | null = null;
    for (const key of this.tokenStorageKeys) {
      raw = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (raw && raw.trim()) break;
    }
    const trimmed = (raw || '').trim();
    if (trimmed) {
      if (/^Bearer\s+/i.test(trimmed)) return trimmed;
      return `Bearer ${trimmed}`;
    }
    return `Bearer ${STATIC_DAY_WISE_JWT}`;
  }

  private collegeTypeQueryValue(schooolOrCollege: string): string {
    const u = (schooolOrCollege || '').toUpperCase();
    if (u === 'SCHOOL') return 'SCHOOL';
    if (u === 'COLLEGE') return 'COLLEGE';
    return schooolOrCollege;
  }

  private campusTypeQueryValue(boardingType: string): string {
    const u = (boardingType || '').toUpperCase();
    if (u === 'DAY_SCHOLAR') return 'DAYSCHOLAR';
    if (u === 'RESIDENTIAL') return 'RESIDENTIAL';
    return boardingType;
  }

  /** Single or multi segment → stable comma list (`COLLEGE,SCHOOL`) for APIs. */
  private collegeTypesWireComma(types: ('SCHOOL' | 'COLLEGE')[]): string {
    const uniq = [...new Set(types.map((c) => this.collegeTypeQueryValue(c)))];
    return uniq.sort().join(',');
  }

  /** Single or multi campus type → stable comma list (`DAYSCHOLAR,RESIDENTIAL`). */
  private boardingTypesWireComma(types: ('RESIDENTIAL' | 'DAY_SCHOLAR')[]): string {
    const uniq = [...new Set(types.map((b) => this.campusTypeQueryValue(b)))];
    return uniq.sort().join(',');
  }

  /**
   * `URLSearchParams.toString()` encodes `,` as `%2C`. Backend expects literal commas in
   * `collegeType`, `campusType`, `stateId`, etc. Encode each comma-separated segment, join with `,`.
   */
  private serializeHygieneQueryParams(qs: URLSearchParams): string {
    /** Same key can appear twice if something used `append`; keep last value and stable order. */
    const order: string[] = [];
    const lastValue = new Map<string, string>();
    qs.forEach((value, key) => {
      if (!lastValue.has(key)) order.push(key);
      lastValue.set(key, value);
    });
    const parts: string[] = [];
    for (const key of order) {
      const value = lastValue.get(key)!;
      const encKey = encodeURIComponent(key);
      const encVal =
        value.indexOf(',') >= 0
          ? value
              .split(',')
              .map((segment) => encodeURIComponent(segment.trim()))
              .filter((s) => s.length > 0)
              .join(',')
          : encodeURIComponent(value);
      parts.push(`${encKey}=${encVal}`);
    }
    return parts.join('&');
  }

  private appendLocationFilters(qs: URLSearchParams, filters?: HygieneLocationFilters): void {
    if (!filters) return;
    if (filters.collegeTypes != null && filters.collegeTypes.length > 0) {
      qs.set('collegeType', this.collegeTypesWireComma(filters.collegeTypes));
    } else if (filters.schooolOrCollege) {
      qs.set('collegeType', this.collegeTypeQueryValue(filters.schooolOrCollege));
    }
    if (filters.boardingTypes != null && filters.boardingTypes.length > 0) {
      qs.set('campusType', this.boardingTypesWireComma(filters.boardingTypes));
    } else if (filters.boardingType) {
      qs.set('campusType', this.campusTypeQueryValue(filters.boardingType));
    }
    if (filters.vendorId != null && String(filters.vendorId).trim() !== '') {
      qs.set('vendorId', String(filters.vendorId).trim());
    }
  }

  getStates(filters?: HygieneLocationFilters): Observable<unknown> {
    const qs = new URLSearchParams();
    this.appendLocationFilters(qs, filters);
    const serialized = this.serializeHygieneQueryParams(qs);
    const suffix = serialized ? `?${serialized}` : '';
    return this.http.get(`${this.statesUrl}${suffix}`, { headers: this.headers });
  }

  getEmpCities(filters?: HygieneLocationFilters): Observable<unknown> {
    const qs = new URLSearchParams();
    this.appendLocationFilters(qs, filters);
    const serialized = this.serializeHygieneQueryParams(qs);
    const suffix = serialized ? `?${serialized}` : '';
    return this.http.get(`${this.empCitiesUrl}${suffix}`, { headers: this.headers });
  }

  /**
   * City list for one or more states (`stateId` comma-separated when multiple).
   * Empty array falls through to callers — prefer `getEmpCities` when no state is selected.
   */
  getCities(stateId: string | number | Array<string | number>, filters?: HygieneLocationFilters): Observable<unknown> {
    const qs = new URLSearchParams();
    const raw = Array.isArray(stateId) ? stateId : [stateId];
    const cleaned = raw.map((x) => String(x).trim()).filter((x) => x.length > 0);
    if (cleaned.length === 0) {
      return this.getEmpCities(filters);
    }
    qs.set('stateId', cleaned.join(','));
    this.appendLocationFilters(qs, filters);
    return this.http.get(`${this.citiesUrl}?${this.serializeHygieneQueryParams(qs)}`, { headers: this.headers });
  }

  /**
   * Campuses for one or more cities (`cityId` comma-separated IDs, same pattern as `stateId` on `getCities`).
   */
  getCampuses(cityId: string | number | Array<string | number>, filters?: HygieneLocationFilters): Observable<unknown> {
    const qs = new URLSearchParams();
    const raw = Array.isArray(cityId) ? cityId : [cityId];
    const cleaned = raw.map((x) => String(x).trim()).filter((x) => x.length > 0);
    if (cleaned.length === 0) {
      return of([]);
    }
    qs.set('cityId', cleaned.join(','));
    this.appendLocationFilters(qs, filters);
    return this.http.get(`${this.campusesUrl}?${this.serializeHygieneQueryParams(qs)}`, { headers: this.headers });
  }

  /**
   * Buildings for one or more campuses (`campusId` comma-separated IDs).
   */
  getBuildings(campusId: string | number | Array<string | number>, filters?: HygieneLocationFilters): Observable<unknown> {
    const qs = new URLSearchParams();
    const raw = Array.isArray(campusId) ? campusId : [campusId];
    const cleaned = raw.map((x) => String(x).trim()).filter((x) => x.length > 0);
    if (cleaned.length === 0) {
      return of([]);
    }
    qs.set('campusId', cleaned.join(','));
    this.appendLocationFilters(qs, filters);
    return this.http.get(`${this.buildingsUrl}?${this.serializeHygieneQueryParams(qs)}`, { headers: this.headers });
  }

  /** GET …/apiV2/vendorList — optional `expHead` (e.g. HOUSEKEEPING from report department). */
  getVendorList(params?: { expHead?: string | null }): Observable<HygieneDayWiseVendor[]> {
    const qs = new URLSearchParams();
    const head = (params?.expHead ?? '').toString().trim();
    if (head !== '') qs.set('expHead', head.toUpperCase());
    const url = qs.toString()
      ? `${this.vendorListUrl}?${this.serializeHygieneQueryParams(qs)}`
      : this.vendorListUrl;
    return this.http.get<unknown>(url, { headers: this.headers }).pipe(map((raw) => this.normalizeVendorList(raw)));
  }

  private normalizeVendorList(res: unknown): HygieneDayWiseVendor[] {
    const list = this.vendorListArrayFromResponse(res);
    return list
      .map((x) => ({
        vendorId: String(x?.vendorId ?? x?.vendor_id ?? x?.id ?? '').trim(),
        vendorName: String(x?.vendorName ?? x?.vendor_name ?? x?.name ?? '').trim()
      }))
      .filter((v) => v.vendorId.length > 0 && v.vendorName.length > 0);
  }

  /** Unwrap common vendorList API shapes: `[]`, `{ data: [] }`, `{ vendorList: [] }`, etc. */
  private vendorListArrayFromResponse(res: unknown): any[] {
    const asArray = (node: unknown): any[] | null => (Array.isArray(node) ? node : null);
    if (res == null) return [];
    const direct = asArray(res);
    if (direct) return direct;
    const r = res as Record<string, unknown>;
    const fromNode = (node: unknown): any[] | null => {
      if (node == null) return null;
      const arr = asArray(node);
      if (arr) return arr;
      if (typeof node === 'object') {
        const o = node as Record<string, unknown>;
        return (
          asArray(o['vendorList']) ??
          asArray(o['vendors']) ??
          asArray(o['data']) ??
          asArray(o['list']) ??
          null
        );
      }
      return null;
    };
    return (
      fromNode(r['data']) ??
      fromNode(r['vendorList']) ??
      fromNode(r['vendors']) ??
      fromNode(r['list']) ??
      fromNode(r) ??
      []
    );
  }

  private setMultiParam(qs: URLSearchParams, key: string, value?: string | string[]): void {
    if (value == null) return;
    const arr = Array.isArray(value) ? value : [value];
    const cleaned = arr.map((x) => String(x).trim()).filter((x) => x.length > 0);
    if (cleaned.length === 0) return;
    qs.set(key, cleaned.join(','));
  }

  getStaffAttendance(params: {
    year: number;
    month: number;
    stateId?: string | string[];
    cityId?: string | string[];
    campusId?: string | string[];
    buildingId?: string | string[];
    departmentName?: string;
    /** Vendor filter when backend supports it on staffAttendance */
    vendorId?: string;
    schooolOrCollege?: string;
    boardingType?: string;
    collegeTypes?: ('SCHOOL' | 'COLLEGE')[];
    boardingTypes?: ('RESIDENTIAL' | 'DAY_SCHOLAR')[];
    /** 1-based page (first = 1). Sent as 0-based `pageNo` on the wire. */
    page?: number;
    /** Page size (sent as `pageSize`). */
    pageSize?: number;
  }): Observable<HygieneStaffAttendanceCampus[]> {
    const qs = new URLSearchParams();
    this.setMultiParam(qs, 'stateId', params.stateId);
    this.setMultiParam(qs, 'cityId', params.cityId);
    this.setMultiParam(qs, 'campusId', params.campusId);
    this.setMultiParam(qs, 'buildingId', params.buildingId);
    if (params.departmentName) qs.set('departmentName', params.departmentName);
    // If vendor not selected, do not send vendorId (keeps existing behavior).
    if (params.vendorId != null && String(params.vendorId).trim() !== '') qs.set('vendorId', String(params.vendorId));
    qs.set('year', String(params.year));
    qs.set('month', String(params.month));
    if (params.collegeTypes != null && params.collegeTypes.length > 0) {
      qs.set('collegeType', this.collegeTypesWireComma(params.collegeTypes));
    } else if (params.schooolOrCollege) {
      qs.set('collegeType', this.collegeTypeQueryValue(params.schooolOrCollege));
    }
    if (params.boardingTypes != null && params.boardingTypes.length > 0) {
      qs.set('campusType', this.boardingTypesWireComma(params.boardingTypes));
    } else if (params.boardingType) {
      qs.set('campusType', this.campusTypeQueryValue(params.boardingType));
    }
    if (params.page != null && Number.isFinite(Number(params.page))) {
      const uiPage = Math.floor(Number(params.page));
      if (uiPage >= 1) qs.set('pageNo', String(Math.max(0, uiPage - 1)));
    }
    if (params.pageSize != null && Number.isFinite(Number(params.pageSize)) && Number(params.pageSize) > 0) {
      qs.set('pageSize', String(Math.floor(Number(params.pageSize))));
    }
    return this.http.get<HygieneStaffAttendanceCampus[]>(
      `${this.staffAttendanceUrl}?${this.serializeHygieneQueryParams(qs)}`,
      {
        headers: this.headers
      }
    );
  }
   getStaffAttendanceExcel(params: {
    year: number;
    month: number;
    stateId?: string | string[];
    cityId?: string | string[];
    campusId?: string | string[];
    buildingId?: string | string[];
    departmentName?: string;
    /** Vendor filter when backend supports it on staffAttendance */
    vendorId?: string;
    schooolOrCollege?: string;
    boardingType?: string;
    collegeTypes?: ('SCHOOL' | 'COLLEGE')[];
    boardingTypes?: ('RESIDENTIAL' | 'DAY_SCHOLAR')[];
  
   
  }): Observable<HygieneStaffAttendanceCampus[]> {
    const qs = new URLSearchParams();
    this.setMultiParam(qs, 'stateId', params.stateId);
    this.setMultiParam(qs, 'cityId', params.cityId);
    this.setMultiParam(qs, 'campusId', params.campusId);
    this.setMultiParam(qs, 'buildingId', params.buildingId);
    if (params.departmentName) qs.set('departmentName', params.departmentName);
    // If vendor not selected, do not send vendorId (keeps existing behavior).
    if (params.vendorId != null && String(params.vendorId).trim() !== '') qs.set('vendorId', String(params.vendorId));
    qs.set('year', String(params.year));
    qs.set('month', String(params.month));
    if (params.collegeTypes != null && params.collegeTypes.length > 0) {
      qs.set('collegeType', this.collegeTypesWireComma(params.collegeTypes));
    } else if (params.schooolOrCollege) {
      qs.set('collegeType', this.collegeTypeQueryValue(params.schooolOrCollege));
    }
    if (params.boardingTypes != null && params.boardingTypes.length > 0) {
      qs.set('campusType', this.boardingTypesWireComma(params.boardingTypes));
    } else if (params.boardingType) {
      qs.set('campusType', this.campusTypeQueryValue(params.boardingType));
    }
   
    return this.http.get<HygieneStaffAttendanceCampus[]>(
      `${this.staffAttendanceExcel}?${this.serializeHygieneQueryParams(qs)}`,
      {
        headers: this.headers
      }
    );
  }

  getStaffAttendanceImages(
    hygieneDate: string,
    campusId: number | string,
    buildingId?: number | string | null,
    /** Matches staffAttendance — uppercased on the wire when present */
    departmentName?: string | null
  ): Observable<StaffAttendanceImage[]> {
    const qs = new URLSearchParams();
    qs.set('hygieneDate', this.hygieneDateParamIso(hygieneDate));
    qs.set('campusId', String(campusId));
    if (buildingId != null && String(buildingId).trim() !== '') qs.set('buildingId', String(buildingId));
    const dn = (departmentName ?? '').toString().trim();
    if (dn !== '') qs.set('departmentName', dn.toUpperCase());
    return this.http.get<StaffAttendanceImage[]>(
      `${this.staffAttendanceImagesUrl}?${this.serializeHygieneQueryParams(qs)}`,
      { headers: this.headers }
    );
  }

  private hygieneDateParamIso(value: string): string {
    if (!value) return value;
    const s = value.toString().trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
      const [d, m, y] = s.split('-');
      return `${y}-${m}-${d}`;
    }
    return s;
  }

}
